import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Users, Plus, Edit2, Trash2, ChevronLeft, ChevronRight,
  CalendarCheck, Wallet, HandCoins, Smartphone, Banknote, Copy, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = {
  production: { label: 'Production', color: 'bg-blue-100 text-blue-800' },
  administration: { label: 'Administration', color: 'bg-purple-100 text-purple-800' },
  transport: { label: 'Transport', color: 'bg-orange-100 text-orange-800' },
  securite: { label: 'Sécurité', color: 'bg-red-100 text-red-800' },
  maintenance: { label: 'Maintenance', color: 'bg-gray-100 text-gray-800' },
  autre: { label: 'Autre', color: 'bg-slate-100 text-slate-800' },
};

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getMonday = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const fmtMoney = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtDateFR = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

const RHPage = () => {
  const { getAuthHeaders } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [weekStart, setWeekStart] = useState(() => fmtISO(getMonday(new Date())));
  const [payrollWeek, setPayrollWeek] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [advances, setAdvances] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [empDialogOpen, setEmpDialogOpen] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [empForm, setEmpForm] = useState({
    matricule: '', full_name: '', phone: '', position: '', department: 'production', daily_rate: '3000',
  });
  const [advDialogOpen, setAdvDialogOpen] = useState(false);
  const [advForm, setAdvForm] = useState({ employee_id: '', amount: '', reason: '' });
  const [waveDialog, setWaveDialog] = useState(null); // {row, deduction}
  const [recapOpen, setRecapOpen] = useState(false);
  const [payingAll, setPayingAll] = useState(false);
  const [deductions, setDeductions] = useState({});
  const [copied, setCopied] = useState('');

  const weekDays = payrollWeek?.days || [];

  const fetchAll = useCallback(async () => {
    try {
      const end = weekDays.length ? weekDays[6] : weekStart;
      const [empRes, weekRes, attRes, advRes, histRes] = await Promise.all([
        axios.get(`${API}/employees`, getAuthHeaders()),
        axios.get(`${API}/payroll/week?start=${weekStart}`, getAuthHeaders()),
        axios.get(`${API}/attendance?start=${weekStart}&end=${addDays(weekStart, 6)}`, getAuthHeaders()),
        axios.get(`${API}/salary-advances`, getAuthHeaders()),
        axios.get(`${API}/payroll/payments?limit=30`, getAuthHeaders()),
      ]);
      setEmployees(empRes.data);
      setPayrollWeek(weekRes.data);
      const attMap = {};
      attRes.data.forEach((a) => { attMap[`${a.employee_id}_${a.date}`] = true; });
      setAttendance(attMap);
      setAdvances(advRes.data);
      setHistory(histRes.data);
      const ded = {};
      weekRes.data.rows.forEach((r) => { ded[r.employee_id] = r.suggested_deduction; });
      setDeductions((prev) => ({ ...ded, ...prev }));
    } catch (error) {
      console.error('Error fetching RH data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setDeductions({}); fetchAll(); }, [fetchAll]);

  function addDays(iso, n) {
    const d = new Date(iso + 'T12:00:00');
    d.setDate(d.getDate() + n);
    return fmtISO(d);
  }

  const changeWeek = (delta) => setWeekStart(addDays(weekStart, delta * 7));

  const toggleDay = async (employeeId, date) => {
    const key = `${employeeId}_${date}`;
    try {
      const res = await axios.post(`${API}/attendance/toggle`, { employee_id: employeeId, date }, getAuthHeaders());
      setAttendance((prev) => ({ ...prev, [key]: res.data.present }));
      const weekRes = await axios.get(`${API}/payroll/week?start=${weekStart}`, getAuthHeaders());
      setPayrollWeek(weekRes.data);
    } catch (error) {
      toast.error('Erreur pointage');
    }
  };

  // ===== Employees CRUD =====
  const openEmpDialog = (emp = null) => {
    setEditingEmp(emp);
    setEmpForm(emp ? {
      matricule: emp.matricule, full_name: emp.full_name, phone: emp.phone || '',
      position: emp.position, department: emp.department, daily_rate: (emp.daily_rate || 0).toString(),
    } : { matricule: '', full_name: '', phone: '', position: '', department: 'production', daily_rate: '3000' });
    setEmpDialogOpen(true);
  };

  const handleEmpSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        full_name: empForm.full_name, phone: empForm.phone || null, position: empForm.position,
        department: empForm.department, daily_rate: parseFloat(empForm.daily_rate) || 0,
      };
      if (editingEmp) {
        await axios.put(`${API}/employees/${editingEmp.id}`, payload, getAuthHeaders());
        toast.success('Employé mis à jour');
      } else {
        await axios.post(`${API}/employees`, { ...payload, matricule: empForm.matricule, salary_type: 'daily' }, getAuthHeaders());
        toast.success('Employé ajouté');
      }
      setEmpDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const toggleEmpActive = async (emp) => {
    try {
      await axios.put(`${API}/employees/${emp.id}`, { is_active: !emp.is_active }, getAuthHeaders());
      toast.success(emp.is_active ? 'Employé désactivé' : 'Employé réactivé');
      fetchAll();
    } catch (error) { toast.error('Erreur'); }
  };

  const deleteEmp = async (emp) => {
    if (!window.confirm(`Supprimer ${emp.full_name} ?`)) return;
    try {
      await axios.delete(`${API}/employees/${emp.id}`, getAuthHeaders());
      toast.success('Employé supprimé');
      fetchAll();
    } catch (error) { toast.error('Erreur'); }
  };

  // ===== Advances =====
  const handleAdvSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/salary-advances`, {
        employee_id: advForm.employee_id, amount: parseFloat(advForm.amount), reason: advForm.reason || null,
      }, getAuthHeaders());
      toast.success('Avance enregistrée');
      setAdvDialogOpen(false);
      setAdvForm({ employee_id: '', amount: '', reason: '' });
      fetchAll();
    } catch (error) { toast.error('Erreur'); }
  };

  const deleteAdvance = async (adv) => {
    if (!window.confirm('Supprimer cette avance ?')) return;
    try {
      await axios.delete(`${API}/salary-advances/${adv.id}`, getAuthHeaders());
      toast.success('Avance supprimée');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // ===== Payments =====
  const recordPayment = async (row, method) => {
    try {
      await axios.post(`${API}/payroll/pay`, {
        employee_id: row.employee_id,
        week_start: weekStart,
        days_worked: row.days_worked,
        daily_rate: row.daily_rate,
        advance_deduction: parseFloat(deductions[row.employee_id]) || 0,
        payment_method: method,
      }, getAuthHeaders());
      toast.success(`Paiement ${method === 'wave' ? 'Wave' : 'espèces'} enregistré pour ${row.full_name}`);
      setWaveDialog(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Annuler ce paiement ?')) return;
    try {
      await axios.delete(`${API}/payroll/payments/${paymentId}`, getAuthHeaders());
      toast.success('Paiement annulé');
      fetchAll();
    } catch (error) { toast.error('Erreur'); }
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(String(text));
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const netFor = (row) => Math.max(0, row.gross - (parseFloat(deductions[row.employee_id]) || 0));

  const markAllToday = async () => {
    const today = fmtISO(new Date());
    try {
      const res = await axios.post(`${API}/attendance/mark-all`, { date: today }, getAuthHeaders());
      toast.success(`${res.data.marked} employé(s) pointé(s) présent(s) aujourd'hui`);
      fetchAll();
    } catch (error) {
      toast.error('Erreur pointage groupé');
    }
  };

  const recapRows = (payrollWeek?.rows || []).filter((r) => !r.payment && r.days_worked > 0 && r.daily_rate > 0);
  const recapTotal = recapRows.reduce((s, r) => s + netFor(r), 0);

  const recapText = () => {
    const lines = recapRows.map((r, i) => `${i + 1}. ${r.full_name} — ${r.phone || 'numéro manquant'} — ${fmtMoney(netFor(r))} F`);
    return `Paie Wave — semaine du ${fmtDateFR(weekStart)} au ${fmtDateFR(addDays(weekStart, 6))}\n${lines.join('\n')}\nTOTAL : ${fmtMoney(recapTotal)} FCFA`;
  };

  const payAllWave = async () => {
    if (!window.confirm(`Enregistrer ${recapRows.length} paiement(s) Wave pour un total de ${fmtMoney(recapTotal)} F ?`)) return;
    setPayingAll(true);
    let ok = 0;
    for (const row of recapRows) {
      try {
        await axios.post(`${API}/payroll/pay`, {
          employee_id: row.employee_id,
          week_start: weekStart,
          days_worked: row.days_worked,
          daily_rate: row.daily_rate,
          advance_deduction: parseFloat(deductions[row.employee_id]) || 0,
          payment_method: 'wave',
        }, getAuthHeaders());
        ok += 1;
      } catch (error) {
        toast.error(`Échec pour ${row.full_name}`);
      }
    }
    setPayingAll(false);
    toast.success(`${ok} paiement(s) Wave enregistré(s)`);
    setRecapOpen(false);
    fetchAll();
  };

  const activeEmployees = employees.filter((e) => e.is_active);
  const pendingAdvances = advances.filter((a) => !a.is_deducted);
  const rows = payrollWeek?.rows || [];
  const totalWeekNet = rows.reduce((s, r) => s + (r.payment ? r.payment.net_salary : netFor(r)), 0);
  const totalPaid = rows.filter((r) => r.payment).reduce((s, r) => s + r.payment.net_salary, 0);

  if (loading) return <div className="flex items-center justify-center h-full">Chargement...</div>;

  const WeekNav = () => (
    <div className="flex items-center gap-2" data-testid="week-navigation">
      <Button variant="outline" size="sm" onClick={() => changeWeek(-1)} data-testid="prev-week-button" className="rounded-sm h-9 w-9 p-0">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <div className="text-sm font-semibold text-[#1B3268] px-2 whitespace-nowrap" data-testid="week-label">
        Semaine du {fmtDateFR(weekStart)} au {fmtDateFR(addDays(weekStart, 6))}
      </div>
      <Button variant="outline" size="sm" onClick={() => changeWeek(1)} data-testid="next-week-button" className="rounded-sm h-9 w-9 p-0">
        <ChevronRight className="w-4 h-4" />
      </Button>
      {weekStart !== fmtISO(getMonday(new Date())) && (
        <Button variant="ghost" size="sm" onClick={() => setWeekStart(fmtISO(getMonday(new Date())))} data-testid="current-week-button" className="text-xs text-slate-500">
          Cette semaine
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="rh-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            RH / Paie
          </h1>
          <p className="text-sm text-slate-600 mt-1">Pointage journalier, paie hebdomadaire et envoi Wave</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Card className="px-4 py-2 bg-[#1B3268] text-white rounded-sm">
            <div className="text-xl font-bold font-mono">{activeEmployees.length}</div>
            <div className="text-xs text-white/70">Employés actifs</div>
          </Card>
          <Card className="px-4 py-2 bg-white border-slate-200 rounded-sm">
            <div className="text-xl font-bold font-mono text-[#1B3268]">{fmtMoney(totalWeekNet)}</div>
            <div className="text-xs text-slate-500">Net semaine (FCFA)</div>
          </Card>
          <Card className="px-4 py-2 bg-white border-slate-200 rounded-sm">
            <div className="text-xl font-bold font-mono text-emerald-600">{fmtMoney(totalPaid)}</div>
            <div className="text-xs text-slate-500">Déjà payé (FCFA)</div>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="pointage" className="w-full">
        <TabsList className="bg-slate-100 rounded-sm">
          <TabsTrigger value="pointage" data-testid="tab-pointage" className="rounded-sm"><CalendarCheck className="w-4 h-4 mr-1.5" />Pointage</TabsTrigger>
          <TabsTrigger value="paie" data-testid="tab-paie" className="rounded-sm"><Wallet className="w-4 h-4 mr-1.5" />Paie</TabsTrigger>
          <TabsTrigger value="employes" data-testid="tab-employes" className="rounded-sm"><Users className="w-4 h-4 mr-1.5" />Employés</TabsTrigger>
          <TabsTrigger value="avances" data-testid="tab-avances" className="rounded-sm"><HandCoins className="w-4 h-4 mr-1.5" />Avances</TabsTrigger>
        </TabsList>

        {/* ==================== POINTAGE ==================== */}
        <TabsContent value="pointage" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <WeekNav />
            <div className="flex items-center gap-3">
              <Button
                onClick={markAllToday}
                data-testid="mark-all-today-button"
                className="bg-[#FDC800] hover:bg-[#e6b600] text-[#1B3268] font-bold rounded-sm h-9"
              >
                <CalendarCheck className="w-4 h-4 mr-2" />Tous présents aujourd'hui
              </Button>
              <p className="text-xs text-slate-500 hidden md:block">Cochez les jours de présence — le salaire se calcule automatiquement</p>
            </div>
          </div>
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-x-auto" data-testid="attendance-grid">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-100 text-slate-700 text-xs uppercase font-semibold">
                  <th className="p-3 text-left">Employé</th>
                  {weekDays.map((d, i) => (
                    <th key={d} className="p-2 text-center min-w-[52px]">
                      <div>{DAY_LABELS[i]}</div>
                      <div className="font-mono text-slate-400">{d.slice(8)}</div>
                    </th>
                  ))}
                  <th className="p-3 text-center">Jours</th>
                  <th className="p-3 text-right">Brut semaine</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="p-6 text-center text-slate-500 text-sm">Aucun employé actif. Ajoutez vos employés dans l'onglet Employés.</td></tr>
                )}
                {rows.map((row) => (
                  <tr key={row.employee_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`attendance-row-${row.employee_id}`}>
                    <td className="p-3">
                      <div className="font-medium text-[#1B3268]">{row.full_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{fmtMoney(row.daily_rate)} F/jour</div>
                    </td>
                    {weekDays.map((d) => {
                      const present = !!attendance[`${row.employee_id}_${d}`];
                      return (
                        <td key={d} className="p-2 text-center">
                          <button
                            onClick={() => toggleDay(row.employee_id, d)}
                            data-testid={`attendance-check-${row.employee_id}-${d}`}
                            className={`w-8 h-8 rounded-sm border transition-all active:scale-90 inline-flex items-center justify-center ${
                              present ? 'bg-[#1B3268] border-[#1B3268] text-white' : 'bg-white border-slate-300 text-transparent hover:border-[#1B3268]'
                            }`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        </td>
                      );
                    })}
                    <td className="p-3 text-center font-mono font-bold" data-testid={`days-count-${row.employee_id}`}>{row.days_worked}</td>
                    <td className="p-3 text-right font-mono font-medium">{fmtMoney(row.gross)} F</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* ==================== PAIE ==================== */}
        <TabsContent value="paie" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <WeekNav />
            <Button
              onClick={() => setRecapOpen(true)}
              disabled={recapRows.length === 0}
              data-testid="wave-recap-button"
              className="bg-[#00b4e6] hover:bg-[#009cc7] text-white font-bold rounded-sm h-9"
            >
              <Smartphone className="w-4 h-4 mr-2" />Récap Wave ({recapRows.length})
            </Button>
          </div>
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-x-auto" data-testid="payroll-table">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-100 text-slate-700 text-xs uppercase font-semibold">
                  <th className="p-3 text-left">Employé</th>
                  <th className="p-3 text-center">Jours</th>
                  <th className="p-3 text-right">Tarif/jour</th>
                  <th className="p-3 text-right">Brut</th>
                  <th className="p-3 text-right">Avance déduite</th>
                  <th className="p-3 text-right">Net à payer</th>
                  <th className="p-3 text-center">Paiement</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const paid = row.payment;
                  return (
                    <tr key={row.employee_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`payroll-row-${row.employee_id}`}>
                      <td className="p-3">
                        <div className="font-medium text-[#1B3268]">{row.full_name}</div>
                        {row.phone && <div className="text-xs text-slate-500 font-mono">{row.phone}</div>}
                      </td>
                      <td className="p-3 text-center font-mono font-bold">{paid ? paid.days_worked : row.days_worked}</td>
                      <td className="p-3 text-right font-mono">{fmtMoney(paid ? paid.daily_rate : row.daily_rate)} F</td>
                      <td className="p-3 text-right font-mono">{fmtMoney(paid ? paid.base_salary : row.gross)} F</td>
                      <td className="p-3 text-right">
                        {paid ? (
                          <span className="font-mono text-red-500">-{fmtMoney(paid.advances_deducted)} F</span>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number"
                              value={deductions[row.employee_id] ?? row.suggested_deduction}
                              onChange={(e) => setDeductions({ ...deductions, [row.employee_id]: e.target.value })}
                              data-testid={`deduction-input-${row.employee_id}`}
                              className="w-24 h-8 text-right font-mono rounded-sm border-slate-300"
                            />
                            {row.advances_due > 0 && (
                              <span className="text-xs text-orange-600" title="Avances en cours">/{fmtMoney(row.advances_due)}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-right font-mono font-bold text-[#1B3268]" data-testid={`net-amount-${row.employee_id}`}>
                        {fmtMoney(paid ? paid.net_salary : netFor(row))} F
                      </td>
                      <td className="p-3 text-center">
                        {paid ? (
                          <div className="flex items-center justify-center gap-2">
                            <Badge className={`rounded-sm ${paid.payment_method === 'wave' ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'}`} data-testid={`paid-badge-${row.employee_id}`}>
                              {paid.payment_method === 'wave' ? <Smartphone className="w-3 h-3 mr-1" /> : <Banknote className="w-3 h-3 mr-1" />}
                              Payé {paid.payment_method === 'wave' ? 'Wave' : 'Espèces'}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => deletePayment(paid.id)} data-testid={`cancel-payment-${row.employee_id}`} className="text-red-500 h-7 w-7 p-0 hover:bg-red-50">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : row.days_worked > 0 ? (
                          <div className="flex items-center justify-center gap-1.5">
                            <Button
                              size="sm"
                              onClick={() => setWaveDialog(row)}
                              data-testid={`pay-wave-button-${row.employee_id}`}
                              className="bg-[#00b4e6] hover:bg-[#009cc7] text-white rounded-sm h-8 text-xs font-bold"
                            >
                              <Smartphone className="w-3.5 h-3.5 mr-1" />Wave
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => window.confirm(`Payer ${fmtMoney(netFor(row))} F en espèces à ${row.full_name} ?`) && recordPayment(row, 'especes')}
                              data-testid={`pay-cash-button-${row.employee_id}`}
                              className="rounded-sm h-8 text-xs border-slate-300"
                            >
                              <Banknote className="w-3.5 h-3.5 mr-1" />Espèces
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Aucun jour pointé</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {/* History */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="payments-history">
            <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-[#1B3268]">Historique des paiements</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs uppercase">
                    <th className="p-2.5 text-left">Semaine</th>
                    <th className="p-2.5 text-left">Employé</th>
                    <th className="p-2.5 text-center">Jours</th>
                    <th className="p-2.5 text-right">Net payé</th>
                    <th className="p-2.5 text-center">Méthode</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr><td colSpan={5} className="p-4 text-center text-slate-400">Aucun paiement enregistré</td></tr>
                  )}
                  {history.map((p) => (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="p-2.5 font-mono text-xs">{fmtDateFR(p.week_start)} → {fmtDateFR(p.week_end)}</td>
                      <td className="p-2.5 font-medium">{p.employee_name}</td>
                      <td className="p-2.5 text-center font-mono">{p.days_worked}</td>
                      <td className="p-2.5 text-right font-mono font-bold">{fmtMoney(p.net_salary)} F</td>
                      <td className="p-2.5 text-center">
                        <Badge variant="outline" className="rounded-sm text-xs">{p.payment_method === 'wave' ? 'Wave' : p.payment_method === 'especes' ? 'Espèces' : 'Autre'}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ==================== EMPLOYES ==================== */}
        <TabsContent value="employes" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openEmpDialog()} data-testid="add-employee-button" className="bg-[#1B3268] hover:bg-[#152a55] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-2" />Nouvel Employé
            </Button>
          </div>
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-x-auto" data-testid="employees-table">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-100 text-slate-700 text-xs uppercase font-semibold">
                  <th className="p-3 text-left">Matricule</th>
                  <th className="p-3 text-left">Nom</th>
                  <th className="p-3 text-left">Téléphone (Wave)</th>
                  <th className="p-3 text-left">Poste</th>
                  <th className="p-3 text-left">Département</th>
                  <th className="p-3 text-right">Tarif journalier</th>
                  <th className="p-3 text-center">Statut</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => {
                  const dept = DEPARTMENTS[emp.department] || DEPARTMENTS.autre;
                  return (
                    <tr key={emp.id} className={`border-b last:border-0 hover:bg-slate-50 ${!emp.is_active ? 'opacity-50' : ''}`} data-testid={`employee-row-${emp.id}`}>
                      <td className="p-3 font-mono text-sm">{emp.matricule}</td>
                      <td className="p-3 font-medium text-[#1B3268]">{emp.full_name}</td>
                      <td className="p-3 font-mono text-sm">{emp.phone || '—'}</td>
                      <td className="p-3 text-sm">{emp.position}</td>
                      <td className="p-3"><Badge className={`rounded-sm ${dept.color}`}>{dept.label}</Badge></td>
                      <td className="p-3 text-right font-mono font-bold">{fmtMoney(emp.daily_rate || 0)} F/j</td>
                      <td className="p-3 text-center">
                        <button onClick={() => toggleEmpActive(emp)} data-testid={`toggle-active-${emp.id}`}>
                          <Badge className={`rounded-sm cursor-pointer ${emp.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                            {emp.is_active ? 'Actif' : 'Inactif'}
                          </Badge>
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEmpDialog(emp)} data-testid={`edit-employee-${emp.id}`} className="h-7 w-7 p-0 text-slate-500 hover:bg-slate-100">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteEmp(emp)} data-testid={`delete-employee-${emp.id}`} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* ==================== AVANCES ==================== */}
        <TabsContent value="avances" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">Les avances en cours sont automatiquement proposées en déduction lors de la paie</p>
            <Button onClick={() => setAdvDialogOpen(true)} data-testid="add-advance-button" className="bg-[#1B3268] hover:bg-[#152a55] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-2" />Nouvelle Avance
            </Button>
          </div>
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-x-auto" data-testid="advances-table">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-100 text-slate-700 text-xs uppercase font-semibold">
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Employé</th>
                  <th className="p-3 text-right">Montant</th>
                  <th className="p-3 text-left">Motif</th>
                  <th className="p-3 text-center">Statut</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {advances.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-400 text-sm">Aucune avance enregistrée</td></tr>
                )}
                {advances.map((adv) => (
                  <tr key={adv.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`advance-row-${adv.id}`}>
                    <td className="p-3 font-mono text-sm">{new Date(adv.date).toLocaleDateString('fr-FR')}</td>
                    <td className="p-3 font-medium">{adv.employee_name}</td>
                    <td className="p-3 text-right font-mono font-bold">{fmtMoney(adv.amount)} F</td>
                    <td className="p-3 text-sm text-slate-600">{adv.reason || '—'}</td>
                    <td className="p-3 text-center">
                      <Badge className={`rounded-sm ${adv.is_deducted ? 'bg-slate-200 text-slate-600' : 'bg-orange-100 text-orange-800'}`}>
                        {adv.is_deducted ? 'Déduite' : 'En cours'}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      {!adv.is_deducted && (
                        <Button variant="ghost" size="sm" onClick={() => deleteAdvance(adv)} data-testid={`delete-advance-${adv.id}`} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          {pendingAdvances.length > 0 && (
            <p className="text-xs text-slate-500">
              Total avances en cours : <span className="font-mono font-bold text-orange-600">{fmtMoney(pendingAdvances.reduce((s, a) => s + a.amount, 0))} FCFA</span>
            </p>
          )}
        </TabsContent>
      </Tabs>

      {/* ==================== WAVE RECAP DIALOG ==================== */}
      <Dialog open={recapOpen} onOpenChange={setRecapOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="wave-recap-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#00b4e6] flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <Smartphone className="w-5 h-5" />Récap Wave — Semaine
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              Copiez la liste, enchaînez les envois dans Wave, puis marquez tout comme payé.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-sm divide-y max-h-72 overflow-y-auto" data-testid="wave-recap-list">
              {recapRows.map((r) => (
                <div key={r.employee_id} className="flex items-center justify-between px-4 py-2.5" data-testid={`recap-row-${r.employee_id}`}>
                  <div>
                    <div className="font-medium text-[#1B3268] text-sm">{r.full_name}</div>
                    <div className={`text-xs font-mono ${r.phone ? 'text-slate-500' : 'text-orange-600'}`}>{r.phone || 'numéro manquant'}</div>
                  </div>
                  <div className="font-mono font-bold text-[#1B3268]">{fmtMoney(netFor(r))} F</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between px-1">
              <span className="text-sm text-slate-600">Total à envoyer</span>
              <span className="font-mono font-bold text-xl text-[#1B3268]" data-testid="wave-recap-total">{fmtMoney(recapTotal)} FCFA</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { copyText(recapText(), 'recap'); toast.success('Liste copiée !'); }}
                data-testid="copy-recap-button"
                className="flex-1 rounded-sm border-slate-300"
              >
                {copied === 'recap' ? <Check className="w-4 h-4 mr-2 text-emerald-600" /> : <Copy className="w-4 h-4 mr-2" />}
                Tout copier
              </Button>
              <Button
                onClick={payAllWave}
                disabled={payingAll}
                data-testid="pay-all-wave-button"
                className="flex-1 bg-[#00b4e6] hover:bg-[#009cc7] text-white font-bold rounded-sm"
              >
                <Check className="w-4 h-4 mr-2" />{payingAll ? 'Enregistrement...' : 'Tout marquer payé'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== WAVE DIALOG ==================== */}
      <Dialog open={!!waveDialog} onOpenChange={(o) => !o && setWaveDialog(null)}>
        <DialogContent className="bg-white" data-testid="wave-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#00b4e6] flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <Smartphone className="w-5 h-5" />Paiement Wave
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              Envoyez le montant via votre application Wave, puis confirmez ici.
            </DialogDescription>
          </DialogHeader>
          {waveDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Bénéficiaire</span>
                  <span className="font-semibold text-[#1B3268]">{waveDialog.full_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Numéro Wave</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold" data-testid="wave-phone">{waveDialog.phone || 'Non renseigné'}</span>
                    {waveDialog.phone && (
                      <Button variant="ghost" size="sm" onClick={() => copyText(waveDialog.phone, 'phone')} data-testid="copy-phone-button" className="h-7 w-7 p-0">
                        {copied === 'phone' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Montant net</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-xl text-[#1B3268]" data-testid="wave-amount">{fmtMoney(netFor(waveDialog))} FCFA</span>
                    <Button variant="ghost" size="sm" onClick={() => copyText(netFor(waveDialog), 'amount')} data-testid="copy-amount-button" className="h-7 w-7 p-0">
                      {copied === 'amount' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-slate-500 border-t pt-2">
                  {waveDialog.days_worked} jour(s) × {fmtMoney(waveDialog.daily_rate)} F − {fmtMoney(parseFloat(deductions[waveDialog.employee_id]) || 0)} F d'avance
                </div>
              </div>
              {!waveDialog.phone && (
                <p className="text-xs text-orange-600">⚠ Aucun numéro renseigné pour cet employé. Ajoutez-le dans l'onglet Employés.</p>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={() => recordPayment(waveDialog, 'wave')}
                  data-testid="confirm-wave-payment-button"
                  className="flex-1 bg-[#00b4e6] hover:bg-[#009cc7] text-white font-bold rounded-sm"
                >
                  <Check className="w-4 h-4 mr-2" />J'ai envoyé via Wave
                </Button>
                <Button variant="outline" onClick={() => setWaveDialog(null)} className="flex-1 rounded-sm">Annuler</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== EMPLOYEE DIALOG ==================== */}
      <Dialog open={empDialogOpen} onOpenChange={setEmpDialogOpen}>
        <DialogContent className="bg-white" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B3268]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {editingEmp ? 'Modifier Employé' : 'Nouvel Employé'}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Le tarif journalier sert au calcul automatique de la paie.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEmpSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Matricule *</Label>
                <Input value={empForm.matricule} onChange={(e) => setEmpForm({ ...empForm, matricule: e.target.value })} required disabled={!!editingEmp}
                  data-testid="employee-matricule-input" className="h-10 border-slate-300 rounded-sm mt-1" />
              </div>
              <div>
                <Label>Nom complet *</Label>
                <Input value={empForm.full_name} onChange={(e) => setEmpForm({ ...empForm, full_name: e.target.value })} required
                  data-testid="employee-name-input" className="h-10 border-slate-300 rounded-sm mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Téléphone (n° Wave)</Label>
                <Input value={empForm.phone} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} placeholder="77 123 45 67"
                  data-testid="employee-phone-input" className="h-10 border-slate-300 rounded-sm mt-1" />
              </div>
              <div>
                <Label>Poste *</Label>
                <Input value={empForm.position} onChange={(e) => setEmpForm({ ...empForm, position: e.target.value })} required
                  data-testid="employee-position-input" className="h-10 border-slate-300 rounded-sm mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Département</Label>
                <Select value={empForm.department} onValueChange={(v) => setEmpForm({ ...empForm, department: v })}>
                  <SelectTrigger data-testid="employee-department-select" className="h-10 border-slate-300 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(DEPARTMENTS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tarif journalier (FCFA) *</Label>
                <Input type="number" step="1" min="0" value={empForm.daily_rate} onChange={(e) => setEmpForm({ ...empForm, daily_rate: e.target.value })} required
                  data-testid="employee-daily-rate-input" className="h-10 border-slate-300 rounded-sm mt-1" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" data-testid="employee-submit-button" className="flex-1 bg-[#1B3268] hover:bg-[#152a55] text-white rounded-sm">
                {editingEmp ? 'Mettre à jour' : 'Ajouter'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setEmpDialogOpen(false)} className="flex-1 rounded-sm">Annuler</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ==================== ADVANCE DIALOG ==================== */}
      <Dialog open={advDialogOpen} onOpenChange={setAdvDialogOpen}>
        <DialogContent className="bg-white" data-testid="advance-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B3268]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Nouvelle Avance</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">L'avance sera proposée en déduction à la prochaine paie.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdvSubmit} className="space-y-4">
            <div>
              <Label>Employé *</Label>
              <Select value={advForm.employee_id} onValueChange={(v) => setAdvForm({ ...advForm, employee_id: v })} required>
                <SelectTrigger data-testid="advance-employee-select" className="h-10 border-slate-300 rounded-sm mt-1">
                  <SelectValue placeholder="Choisir un employé" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {activeEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant (FCFA) *</Label>
              <Input type="number" step="1" min="1" value={advForm.amount} onChange={(e) => setAdvForm({ ...advForm, amount: e.target.value })} required
                data-testid="advance-amount-input" className="h-10 border-slate-300 rounded-sm mt-1" />
            </div>
            <div>
              <Label>Motif</Label>
              <Input value={advForm.reason} onChange={(e) => setAdvForm({ ...advForm, reason: e.target.value })} placeholder="Optionnel"
                data-testid="advance-reason-input" className="h-10 border-slate-300 rounded-sm mt-1" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={!advForm.employee_id} data-testid="advance-submit-button" className="flex-1 bg-[#1B3268] hover:bg-[#152a55] text-white rounded-sm">
                Enregistrer
              </Button>
              <Button type="button" variant="outline" onClick={() => setAdvDialogOpen(false)} className="flex-1 rounded-sm">Annuler</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RHPage;
